/// <amd-module name="Wit/Common/WorkItemFieldService/IWorkItemFieldService" />
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";

export interface IFieldAllowedValuesResponse {
    data?: string[];
    errorMessage?: string;
}

export interface IFieldResponse {
    data?: IFieldEntry[];
    errorMessage?: string;
}

export interface IFieldAllowedValuesRequest {
    fieldRefName: string;
    projectId?: string;
    workItemTypeName?: string;
}

export interface IWorkItemFieldService extends IVssService {
    /**
     * Get allowed values for a field, optionally specifying a project and work item type
     */
    getAllowedValues(fieldRefName: string, projectId?: string, workItemTypeName?: string): Promise<string[] | undefined>;
    /**
     * Get a field object from a field id
     */
    getField(id: number): IFieldEntry | undefined;
    /**
     * Get a field object from a field reference name
     */
    getFieldByReferenceName(referenceName: string): IFieldEntry | undefined;
    /**
     * Load fields when they were included in page load
     */
    getFields(): IFieldEntry[] | undefined;
    /**
     * Load fields with an asynchronous server call
     */
    getFieldsAsync(): Promise<IFieldEntry[] | undefined>;
    /**
     * Get the error message if no data is returned for fields
     */
    getFieldsErrorMessage(): string | undefined;
    /**
     * Get the error message if no data is returned for allowed values
     */
    getFieldAllowedValuesErrorMessage(): string | undefined;
}

class WorkItemFieldService extends VssService implements IWorkItemFieldService {
    private allowedValuesCache: { [key: string]: Promise<string[] | undefined> } = {};
    private fields: IFieldEntry[] | undefined;
    private fieldsById: { [key: number]: IFieldEntry } = {};
    private fieldsByReferenceName: { [key: string]: IFieldEntry } = {};
    private fieldsDataProviderError: string | undefined;
    private allowedValuesDataProviderError: string | undefined;

    public getField(id: number): IFieldEntry | undefined {
        this.getFields();
        return this.fieldsById[id];
    }

    public getFieldByReferenceName(referenceName: string): IFieldEntry | undefined {
        this.getFields();
        return this.fieldsByReferenceName[referenceName];
    }

    public getFields(): IFieldEntry[] | undefined {
        if (!this.fields) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const fieldsData = contributionService.getData<IFieldResponse>(FieldDataProviders.FieldsDataProvider);
            this.loadFields(fieldsData);
        }
        if ($DEBUG && !this.fields) {
            console.warn(`Tried to synchronously getFields() but data provider ${FieldDataProviders.FieldsDataProvider} not found.`);
        }

        return this.fields;
    }

    public async getFieldsAsync(): Promise<IFieldEntry[] | undefined> {
        if (!this.fields) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const fieldsData = await contributionService.getDataAsync<IFieldResponse>(FieldDataProviders.FieldsDataProvider);
            this.loadFields(fieldsData);
        }

        return this.fields;
    }

    public async getAllowedValues(fieldRefName: string, projectId?: string, workItemTypeName?: string): Promise<string[] | undefined> {
        const cacheKey = getCacheKey(projectId, fieldRefName, workItemTypeName);
        let promise: Promise<string[] | undefined> | undefined;
        if (!this.allowedValuesCache[cacheKey]) {
            // check for pseudo tag fields (System.Tag-Add and System.Tag-Remove)
            if (fieldRefName.startsWith(CoreFieldRefNames.Tags)) {
                promise = projectId ? this.getTagsSuggestedValues(projectId).then(data => data ?? new Array<string>()) : Promise.resolve([]);
            } else {
                const requestParams = {
                    projectId,
                    fieldRefName,
                    workItemTypeName
                };
                promise = this.loadAllowedValues(requestParams);
            }
            this.allowedValuesCache[cacheKey] = promise;
        }

        return promise || this.allowedValuesCache[cacheKey];
    }

    public getFieldsErrorMessage(): string | undefined {
        if (this.fieldsDataProviderError) {
            return this.fieldsDataProviderError;
        }

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const exception = contributionService.getDataProviderExceptions()[FieldDataProviders.FieldsDataProvider];
        return exception?.message;
    }

    public getFieldAllowedValuesErrorMessage(): string | undefined {
        if (this.allowedValuesDataProviderError) {
            return this.allowedValuesDataProviderError;
        }

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const exception = contributionService.getDataProviderExceptions()[FieldDataProviders.FieldAllowedValuesDataProvider];
        return exception?.message;
    }

    private getTagsSuggestedValues(projectId: string): Promise<string[] | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const requestParams = {
            projectScope: projectId
        };
        return contributionService.getDataAsync<string[]>(
            FieldDataProviders.WorkItemTagSuggestionsDataProvider,
            requestParams,
            /* ignoreCache */ true
        );
    }

    private async loadAllowedValues(requestParams: IFieldAllowedValuesRequest): Promise<string[] | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const response = await contributionService.getDataAsync<IFieldAllowedValuesResponse>(
            FieldDataProviders.FieldAllowedValuesDataProvider,
            requestParams,
            /* ignoreCache */ true
        );

        if (response?.data) {
            return response.data;
        } else if (response?.errorMessage) {
            this.allowedValuesDataProviderError = response.errorMessage;
        }
    }

    private async loadFields(fieldsData?: IFieldResponse) {
        if (fieldsData?.data) {
            this.fields = [...fieldsData.data];
            this.fields.forEach(field => {
                this.fieldsById[field.id] = field;
                this.fieldsByReferenceName[field.referenceName] = field;
            });
        } else if (fieldsData?.errorMessage) {
            this.fieldsDataProviderError = fieldsData.errorMessage;
        }
    }
}

const serviceName = "IWorkItemFieldService";
Services.add(serviceName, { serviceFactory: WorkItemFieldService });

export function getWorkItemFieldService(pageContext: IVssPageContext): IWorkItemFieldService {
    return pageContext.getService<IWorkItemFieldService>(serviceName);
}

const enum FieldDataProviders {
    FieldAllowedValuesDataProvider = "ms.vss-work-web.fields-allowed-values-provider",
    FieldsDataProvider = "ms.vss-work-web.fields-data-provider",
    WorkItemTagSuggestionsDataProvider = "ms.vss-work-web.work-item-tag-suggestions-data-provider"
}

function getCacheKey(projectId?: string, fieldName?: string, workItemTypeName?: string): string {
    if (projectId || fieldName || workItemTypeName) {
        return [projectId, fieldName, workItemTypeName].filter(v => v).join("-");
    } else {
        return "global";
    }
}
